/* Carte d'accès au chat libre de l'enfant, sur sa fiche parent : le */
/* consentement s'y donne et s'y révoque, et c'est de là qu'on ouvre le chat. */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '../lib/routing';
import { ChatCircleDots, Eye, Microphone, Trash } from '@phosphor-icons/react';
import { formatSpeechTime } from '../lib/student-voice';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '../lib/api';
import { useRoles } from '../hooks/use-roles';

interface Access {
  classroom_student_id: string;
  first_name: string;
  level: string | null;
  enabled: boolean;
  daily_turn_cap: number;
  voice_daily_seconds: number;
  voice_seconds_used_today: number;
  turns_used_today: number;
}

export default function StudentChatCard({ studentId }: { studentId: string }) {
  const navigate = useNavigate();
  const { refreshChildren } = useRoles();
  const [access, setAccess] = useState<Access | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmErase, setConfirmErase] = useState(false);
  const [erased, setErased] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.parentOrTutor.student.chatAccess
      .query({ studentId })
      .then((a) => setAccess(a as Access))
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [studentId]);

  useEffect(() => load(), [load]);

  const toggle = async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const a = await api.parentOrTutor.student.setChatAccess.mutate({ studentId, enabled });
      setAccess(a as Access);
      /* Le rail lit l'activation depuis les enfants liés, chargés au montage de
         la session : sans ce rechargement, l'entrée n'apparaîtrait qu'au
         prochain refresh de la page. */
      await refreshChildren();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  /* L'effacement appartient au responsable, pas à l'enfant : le chat lui
     annonce que ses parents peuvent le lire, et un bouton d'effacement à sa
     portée rendrait cette annonce fausse. */
  const eraseAll = async () => {
    setErasing(true);
    setError(null);
    try {
      const threads = (await api.parentOrTutor.student.chatThreads.query({ studentId })) as { id: string }[];
      for (const t of threads) {
        await api.parentOrTutor.student.deleteChatThread.mutate({ studentId, threadId: t.id });
      }
      setErased(true);
      setConfirmErase(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setErasing(false);
    }
  };

  if (loading) return <Skeleton className="h-[132px] rounded-card mb-4" />;
  if (!access) return null;

  return (
    <div className="border border-border bg-card rounded-card p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-10 h-10 rounded-full bg-naia-info-bg text-naia-blue flex items-center justify-center">
          <ChatCircleDots size={20} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-[14px] text-foreground">Discuter avec Naïa</div>
          <p className="text-[13px] text-muted-foreground mt-1 leading-[1.5]">
            {access.first_name} peut poser librement ses questions à la tutrice. Naïa explique et fait
            chercher — elle ne fait pas les devoirs à sa place.
          </p>
          <p className="text-[13px] text-muted-foreground mt-2 leading-[1.5]">
            Naïa s'appuie sur le programme officiel de l'Éducation nationale
            {access.level ? ` et cale ses explications sur le niveau ${access.level}` : ''}. Ce niveau
            est un repère, pas une limite : {access.first_name} peut poser des questions plus avancées.
          </p>
        </div>
      </div>

      {error && <Alert className="mt-3">{error}</Alert>}

      {access.enabled ? (
        <>
          <p className="flex items-center gap-[6px] text-[12px] text-muted-foreground mt-3">
            <Eye size={13} weight="bold" className="shrink-0" />
            Vous pouvez lire toutes les conversations. {access.first_name} en est informé·e.
          </p>
          <p className="text-[12px] text-muted-foreground mt-1">
            {access.turns_used_today} / {access.daily_turn_cap} messages écrits aujourd'hui.
          </p>
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2">
              <Microphone size={15} weight="bold" className="shrink-0 text-muted-foreground" />
              <span className="font-bold text-[13px] text-foreground">Parler avec Naïa</span>
            </div>
            <p className="text-[12px] text-muted-foreground mt-1">
              {formatSpeechTime(access.voice_seconds_used_today)} utilisées sur{' '}
              {formatSpeechTime(access.voice_daily_seconds)} par jour.
            </p>
          </div>
          <div className="flex gap-[10px] flex-wrap mt-3">
            <Button onClick={() => navigate(`/tutor/enfant/${studentId}/chat`)}>Ouvrir le chat</Button>
            <Button variant="secondary" onClick={() => void toggle(false)} disabled={busy}>
              Désactiver
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Ce que le produit fait, et ce qu'il ne fait pas. Le second point
              n'est pas une précaution de style : sans lui, activer vaudrait
              promesse de surveillance, que nous ne tenons pas. */}
          <ul className="text-[12.5px] text-muted-foreground mt-3 leading-[1.6] list-disc pl-[18px]">
            <li>Les conversations sont enregistrées et vous sont accessibles.</li>
            <li>Sur un sujet sensible, Naïa renvoie vers un adulte et vers le 3018 / 119.</li>
            <li>
              Naïa <strong>ne surveille pas</strong> {access.first_name} et ne vous alerte pas : elle
              ne remplace pas un adulte.
            </li>
            <li>
              Environ {access.daily_turn_cap} messages écrits et{' '}
              {formatSpeechTime(access.voice_daily_seconds)} de parole par jour.
            </li>
          </ul>
          <Button className="mt-3" onClick={() => void toggle(true)} disabled={busy}>
            {busy ? 'Activation…' : 'Activer le chat'}
          </Button>
        </>
      )}

      {erased ? (
        <p className="text-[12px] text-muted-foreground mt-3">Conversations effacées.</p>
      ) : confirmErase ? (
        <div className="mt-3 border border-border rounded-card p-3">
          <p className="text-[12.5px] text-foreground leading-[1.5]">
            Effacer définitivement toutes les conversations de {access.first_name} ? Les messages sont
            supprimés de nos serveurs et ne peuvent pas être récupérés.
          </p>
          <div className="flex gap-[10px] flex-wrap mt-3">
            <Button variant="secondary" onClick={() => void eraseAll()} disabled={erasing}>
              {erasing ? 'Effacement…' : 'Effacer définitivement'}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmErase(false)} disabled={erasing}>
              Annuler
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmErase(true)}
          className="flex items-center gap-[6px] text-[12px] text-muted-foreground underline underline-offset-2 mt-3 bg-transparent border-none p-0 cursor-pointer min-h-[44px]"
        >
          <Trash size={13} weight="bold" className="shrink-0" />
          Effacer les conversations
        </button>
      )}
    </div>
  );
}
